import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AppContext } from '@umss/shared/core';
import {
  AuthService,
  FacilityDto,
  PagedResultDto,
  StripePaymentMethod
} from '@umss/shared/data-access';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom
} from 'rxjs';
import { FacilityService } from './facility.service';
import { FacilityStripeService } from './facility-stripe.service';
import { showDevWarning } from '@umss/shared/utils';

@Injectable({ providedIn: 'root' })
export class FacilityContextService implements AppContext {
  //private readonly MOCK_FACILITY_ID = '650030a4-9a7d-4813-aad5-5cb8e6b39964';

  private authService = inject(AuthService);
  private facilityService = inject(FacilityService);
  private stripeService = inject(FacilityStripeService);

  private readonly INITIAL_VALUE = '';
  private readonly STORAGE_KEY = 'umss_selected_facility_id';

  /**
   * Overrides the facility ID when the user manually selects a facility.
   * Seeded from localStorage so the selection survives a page reload.
   */
  private _selectedFacilityId = signal<string | null>(
    localStorage.getItem(this.STORAGE_KEY)
  );

  /**
   * Facility id that is computed based on the auth state.
   * If you account does not have a FacilityId, then replace the this.INITIAL_VALUE by
   * the MOCK_FACILITY_ID but only for testing purposes.
   */
  facilityId = computed(() => {
    const override = this._selectedFacilityId();
    if (override) return override;
    return this.authService.authState().facilityIds[0] || this.INITIAL_VALUE;
  });

  /**
   * Flag used by the AuthRefresh provider to check if the FacilityContext has loaded
   * It's mark as ready when it is different from the initial value
   */
  readonly ready = computed(
    () => this.facilityId().trim() !== this.INITIAL_VALUE
  );

  /**
   * Facility object of the logged-in user.
   * This is automatically loaded when the facilityId changes.
   */
  private _facility = signal<FacilityDto | null>(null);
  readonly facility = this._facility.asReadonly();

  /**
   * Loading state for the facility data
   */
  private _loadingFacility = signal<boolean>(false);
  readonly loadingFacility = this._loadingFacility.asReadonly();

  /**
   * List of all facilities accessible to the logged-in user.
   * Populated after login when the user has access to more than one facility.
   */
  private _availableFacilities = signal<FacilityDto[]>([]);
  readonly availableFacilities = this._availableFacilities.asReadonly();

  private _hasMoreFacilities = signal(false);
  readonly hasMoreFacilities = this._hasMoreFacilities.asReadonly();

  private _loadingFacilities = signal(false);
  readonly loadingFacilities = this._loadingFacilities.asReadonly();

  private _facilitiesCurrentPage = 0;

  /**
   * Payment methods for the facility.
   * Automatically loaded when the facilityId changes.
   */
  private _paymentMethods = signal<StripePaymentMethod[]>([]);
  readonly paymentMethods = this._paymentMethods.asReadonly();

  /**
   * Whether the facility has at least one payment method configured.
   */
  readonly hasPaymentMethod = computed(
    () => !this._loadingPaymentMethods() && this._paymentMethods().length > 0
  );

  /**
   * Whether billing is managed by the facility's parent corporation.
   * When true, the facility doesn't need its own payment methods to perform billable actions.
   */
  readonly isBillingManagedByCorporate = computed(
    () => this._facility()?.billingManagedByCorporate ?? false
  );

  /**
   * Whether the facility can perform billable actions (post shifts, etc.).
   * True if either billing is managed by corporate OR the facility has its own payment method.
   */
  readonly hasActivePaymentMethod = computed(
    () => this.isBillingManagedByCorporate() || this.hasPaymentMethod()
  );

  /**
   * The default payment method, or null if none is set.
   */
  readonly defaultPaymentMethod = computed(
    () => this._paymentMethods().find((pm) => pm.isDefault) ?? null
  );

  private _loadingPaymentMethods = signal<boolean>(false);
  readonly loadingPaymentMethods = this._loadingPaymentMethods.asReadonly();

  // Shared in-flight promise so bootstrap (ensureLoaded) and the in-session
  // login subscription deduplicate when both fire for the same facilityId.
  private _loadInFlight: Promise<void> | null = null;
  private _loadedId: string | null = null;

  constructor() {
    const facilityId$ = toObservable(this.facilityId).pipe(
      filter((id) => !!id && id !== this.INITIAL_VALUE),
      distinctUntilChanged()
    );

    const hasToken$ = toObservable(
      computed(() => !!this.authService.accessToken)
    ).pipe(filter(Boolean));

    combineLatest([facilityId$, hasToken$])
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(([prevId], [currId]) => prevId === currId)
      )
      .subscribe(([id]) => {
        this.loadFacilityContext(id);
      });

    toObservable(computed(() => this.authService.authState().facilityIds))
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged((a, b) => a.join(',') === b.join(','))
      )
      .subscribe((ids) => {
        const { corporateId } = this.authService.authState();
        const hasCorporateAccess = !!corporateId && corporateId.trim() !== '';

        // If the stored selection no longer belongs to this user's facility list, discard it.
        // We only do this if:
        // 1. We have a non-empty ids list to check against
        // 2. The user is NOT a corporate admin (who has access to all facilities in the corp)
        const stored = this._selectedFacilityId();
        if (
          stored &&
          ids.length > 0 &&
          !ids.includes(stored) &&
          !hasCorporateAccess
        ) {
          this._selectedFacilityId.set(null);
          localStorage.removeItem(this.STORAGE_KEY);
        }

        this._availableFacilities.set([]);
        this._facilitiesCurrentPage = 0;
        this._hasMoreFacilities.set(false);

        // Load facilities if user has multiple assigned or if it's a corporate admin
        if (ids.length > 1 || hasCorporateAccess) {
          this.loadNextFacilities();
        }
      });
  }

  /**
   * Reloads the facility data from the API.
   * Call this after operations that change facility properties (e.g. supported scopes).
   */
  reloadFacility(): void {
    const id = this.facilityId();
    if (id) {
      this._loadedId = null;
      this.loadFacility(id);
    }
  }

  /**
   * Synchronously inspects the auth state and triggers facility + payment-methods
   * fetches if a facilityId is present. Used by the bootstrap to avoid the
   * signal → toObservable → effect-flush race during `provideAppInitializer`.
   */
  ensureLoaded(): Promise<void> {
    const id = this.facilityId();
    showDevWarning('[FacilityContextService] ensureLoaded called', {
      facilityId: id,
      _loadedId: this._loadedId,
      _facility: this._facility(),
      _loadInFlightExists: !!this._loadInFlight
    });
    if (!id) return Promise.resolve();
    if (this._loadedId === id && this._facility()) return Promise.resolve();
    return this.loadFacilityContext(id);
  }

  private loadFacilityContext(id: string): Promise<void> {
    if (this._loadInFlight) return this._loadInFlight;

    this._loadInFlight = Promise.all([
      this.loadFacility(id),
      this.loadPaymentMethods(id)
    ])
      .then(() => {
        this._loadedId = id;
      })
      .finally(() => {
        this._loadInFlight = null;
      });

    return this._loadInFlight;
  }

  /**
   * Manually select which facility is active.
   * The chosen ID overrides the default (facilityIds[0] from auth state).
   */
  selectFacility(id: string): void {
    localStorage.setItem(this.STORAGE_KEY, id);
    this._selectedFacilityId.set(id);
  }

  /**
   * Loads the next page of accessible facilities.
   * Call this on initial load or when the user scrolls to the end of the selector list.
   */
  loadNextFacilities(): void {
    if (this._loadingFacilities()) return;

    const nextPage = this._facilitiesCurrentPage + 1;
    const { corporateId } = this.authService.authState();

    this._loadingFacilities.set(true);

    this.facilityService
      .getAllFacilities({
        corporateId: corporateId ?? undefined,
        pageNumber: nextPage,
        pageSize: 20
      })
      .subscribe({
        next: (result: PagedResultDto<FacilityDto>) => {
          this._availableFacilities.update((prev) => [
            ...prev,
            ...result.items
          ]);
          this._facilitiesCurrentPage = nextPage;
          this._hasMoreFacilities.set(result.hasNextPage);
          this._loadingFacilities.set(false);
        },
        error: () => {
          this._loadingFacilities.set(false);
        }
      });
  }

  private loadFacility(id: string): Promise<void> {
    this._loadingFacility.set(true);

    return firstValueFrom(this.facilityService.getFacilityById(id))
      .then((facility) => {
        this._facility.set(facility);
      })
      .catch(() => {
        this._facility.set(null);
      })
      .finally(() => {
        this._loadingFacility.set(false);
      });
  }

  /**
   * Reloads payment methods for the current facility.
   * Call this after adding or removing a payment method.
   */
  reloadPaymentMethods(): void {
    const id = this.facilityId();
    if (id) this.loadPaymentMethods(id);
  }

  private loadPaymentMethods(facilityId: string): Promise<void> {
    this._loadingPaymentMethods.set(true);

    return firstValueFrom(this.stripeService.getPaymentMethods(facilityId))
      .then((methods) => {
        this._paymentMethods.set(methods);
      })
      .catch(() => {
        this._paymentMethods.set([]);
      })
      .finally(() => {
        this._loadingPaymentMethods.set(false);
      });
  }
}
